import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client'; 
import { 
  CheckCircle2, MapPin, Phone, LogOut, Car, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface Booking {
  id: string;
  name: string;
  phone: string;
  address: string;
  package_tier: string;
  vehicle_size: string;
  total_price: number;
  service_date: string;
  status: 'pending' | 'completed';
}

const MASTER_PIN = "1234";

export function JobsDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);

  // Memoize the client so it doesn't trigger effect re-runs
  const supabase = useMemo(() => createClient(), []);

  const fetchBookings = useCallback(async () => {
    // Only run if we aren't already loading
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('service_date', { ascending: true });

      if (error) throw error;
      setBookings((data as Booking[]) || []);
    } catch {
      toast.error("Failed to fetch manifest");
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Sync effect: Only triggers when authentication status changes
  useEffect(() => {
    let active = true;
    if (isAuthenticated && active) {
      fetchBookings();
    }
    return () => { active = false; };
  }, [isAuthenticated, fetchBookings]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === MASTER_PIN) {
      setIsAuthenticated(true);
      toast.success("Vault Access Granted");
    } else {
      toast.error("Invalid Access Key");
    }
  };

  async function toggleStatus(id: string, currentStatus: string) {
    const nextStatus = currentStatus === 'pending' ? 'completed' : 'pending';
    const { error } = await supabase
      .from('bookings')
      .update({ status: nextStatus })
      .eq('id', id);

    if (error) {
      toast.error("Update failed");
    } else {
      toast.success(`Job marked as ${nextStatus}`);
      fetchBookings();
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 text-slate-100">
        <form onSubmit={handleLogin} className="w-full max-w-sm space-y-6 text-center">
          <div className="inline-block p-4 bg-purple-600 rounded-3xl mb-4 shadow-xl">
            <Car className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-black uppercase italic tracking-tighter">VBS | Operator Login</h1>
          <input 
            type="password" 
            placeholder="ACCESS KEY" 
            className="w-full h-16 bg-slate-900 border-2 border-slate-800 rounded-2xl text-center font-black tracking-[0.5em] focus:border-purple-600 outline-none transition-all text-white"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
          <Button type="submit" className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase rounded-2xl shadow-lg">
            Authorize Access
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-black text-slate-900 italic uppercase tracking-tighter leading-none">VBS Manifest</h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Operator Console</p>
        </div>
        <button onClick={() => setIsAuthenticated(false)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <main className="p-4 max-w-2xl mx-auto space-y-4">
        {loading && bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            <p className="font-black uppercase text-slate-400 text-[10px] tracking-widest">Scanning Vault...</p>
          </div>
        ) : bookings.length === 0 ? (
          <p className="text-center py-20 font-black uppercase text-slate-400 text-xs tracking-widest italic">No active bookings found.</p>
        ) : (
          bookings.map((job) => (
            <div 
              key={job.id} 
              className={`bg-white rounded-[2rem] border-2 transition-all p-6 ${
                job.status === 'completed' ? 'opacity-50 border-slate-100 grayscale' : 'border-white shadow-sm shadow-slate-200/50'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                    job.status === 'completed' ? 'bg-slate-100 text-slate-500' : 'bg-teal-100 text-teal-700'
                  }`}>
                    {job.status}
                  </span>
                  <h3 className="text-xl font-black text-slate-900 uppercase italic mt-1">{job.name}</h3>
                  <p className="text-[10px] font-bold text-purple-600 uppercase tracking-tight">{job.package_tier} • {job.vehicle_size}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-slate-900 leading-none">${job.total_price}</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">{job.service_date}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                <a href={`tel:${job.phone}`} className="flex items-center justify-center gap-2 h-12 bg-slate-50 rounded-xl text-slate-900 font-black text-[10px] uppercase border border-slate-100 active:bg-slate-200 transition-colors">
                  <Phone className="w-3 h-3 text-teal-600" /> Call
                </a>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 h-12 bg-slate-50 rounded-xl text-slate-900 font-black text-[10px] uppercase border border-slate-100 active:bg-slate-200 transition-colors">
                  <MapPin className="w-3 h-3 text-purple-600" /> Navigate
                </a>
              </div>

              <Button 
                onClick={() => toggleStatus(job.id, job.status)}
                className={`w-full h-14 rounded-2xl font-black uppercase tracking-widest ${
                  job.status === 'completed' 
                    ? 'bg-slate-200 text-slate-600' 
                    : 'bg-teal-500 text-white hover:bg-teal-600 shadow-md active:scale-[0.98]'
                }`}
              >
                {job.status === 'completed' ? 'Re-open Job' : 'Complete Job'}
                {job.status !== 'completed' && <CheckCircle2 className="ml-2 w-4 h-4" />}
              </Button>
            </div>
          ))
        )}
      </main>
    </div>
  );
}